import { Injectable, Injector } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { BehaviorSubject } from 'rxjs';
import { createServiceLogger } from '../utils/logger.util';
import { LazyGlitchTipService } from './lazy-glitchtip.service';
import { ErrorHandlerService } from './error-handler.service';

export interface StorageStatus {
  isConnected: boolean;
  driver: string;
  connectionAttempts: number;
  lastError?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppStorageService {
  private _storage: Storage | null = null;
  private logger = createServiceLogger('AppStorageService');
  private connectionAttempts = 0;
  private readonly MAX_CONNECTION_ATTEMPTS = 3;
  private readonly CONNECTION_RETRY_DELAY = 1000;
  private oldImageCleanupScheduled = false;

  // Global queue for IndexedDB operations
  private operationQueue: Promise<void> = Promise.resolve();

  private storageStatusSubject = new BehaviorSubject<StorageStatus>({
    isConnected: false,
    driver: 'unknown',
    connectionAttempts: 0,
  });

  readonly storageStatus$ = this.storageStatusSubject.asObservable();

  constructor(
    private storage: Storage,
    private glitchTip: LazyGlitchTipService,
    private errorHandler: ErrorHandlerService,
    private injector?: Injector,
  ) {
    this.init();
  }

  async init(): Promise<void> {
    if (this._storage) {
      return;
    }

    return this.initWithRecovery();
  }

  private async initWithRecovery(): Promise<void> {
    this.connectionAttempts++;

    try {
      const storageInstance = await this.storage.create();
      this._storage = storageInstance;

      const currentDriver = this._storage.driver;

      this.logger.info('Storage initialized successfully', {
        operation: 'storage_init',
        metadata: {
          driver: currentDriver,
          attempt: this.connectionAttempts,
        },
      });

      // Update storage status
      this.updateStorageStatus({
        isConnected: true,
        driver: currentDriver,
        connectionAttempts: this.connectionAttempts,
      });

      this.scheduleOldImageCleanup();
    } catch (error) {
      this.logger.error(
        'Failed to initialize storage',
        {
          operation: 'storage_init_failed',
          metadata: { attempt: this.connectionAttempts },
        },
        error,
      );

      // Update storage status with error
      this.updateStorageStatus({
        isConnected: false,
        driver: 'unknown',
        connectionAttempts: this.connectionAttempts,
        lastError: error instanceof Error ? error.message : String(error),
      });

      if (this.connectionAttempts < this.MAX_CONNECTION_ATTEMPTS) {
        this.logger.info('Retrying storage initialization', {
          operation: 'storage_init_retry',
          metadata: {
            attempt: this.connectionAttempts,
            nextAttemptIn: this.CONNECTION_RETRY_DELAY,
          },
        });

        await this.delay(this.CONNECTION_RETRY_DELAY);
        return this.initWithRecovery();
      }

      this.logger.error(
        'Critical: Storage initialization failed after all attempts',
        {
          operation: 'storage_init_critical_failure',
          metadata: { totalAttempts: this.connectionAttempts },
        },
        error,
      );

      this.glitchTip.captureStorageError(error as Error, {
        operation: 'init',
        storageType: 'ionic_storage',
        retryAttempt: this.connectionAttempts,
      });

      throw error;
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private updateStorageStatus(status: StorageStatus): void {
    this.storageStatusSubject.next(status);
  }

  private scheduleOldImageCleanup(): void {
    if (this.oldImageCleanupScheduled || !this.injector) {
      return;
    }

    this.oldImageCleanupScheduled = true;
    queueMicrotask(() => {
      this.runOldImageCleanup().catch((error) => {
        this.logger.warn('Failed to run scheduled old image cleanup', {
          operation: 'scheduled_gallery_cleanup_failed',
          metadata: { error: error instanceof Error ? error.message : String(error) },
        });
      });
    });
  }

  private async runOldImageCleanup(): Promise<void> {
    const { GalleryService } = await import('./gallery.service');
    const galleryService = this.injector?.get(GalleryService);
    await galleryService?.cleanupOldImages();
  }

  private async executeWithRecovery<T>(operation: () => Promise<T>, operationName: string): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      // Check if this is an IndexedDB connection error
      if (this.isIndexedDBConnectionError(error)) {
        // Prevent multiple simultaneous recovery attempts
        if (this.isRecovering) {
          this.logger.warn(`Recovery already in progress for ${operationName}, waiting...`, {
            operation: 'storage_recovery_waiting',
            metadata: { operationName },
          });

          // Wait for current recovery to complete
          let retries = 0;
          while (this.isRecovering && retries < 30) {
            // Wait up to 3 seconds
            await new Promise((resolve) => setTimeout(resolve, 100));
            retries++;
          }

          // Retry the operation after recovery
          if (!this.isRecovering) {
            try {
              return await operation();
            } catch (retryError) {
              throw retryError;
            }
          }
        }

        this.isRecovering = true;

        try {
          this.logger.warn(`IndexedDB connection error in ${operationName}, attempting recovery`, {
            operation: 'storage_recovery_attempt',
            metadata: {
              operationName,
              error: error instanceof Error ? error.message : String(error),
            },
          });

          // Add breadcrumb for IndexedDB error and recovery
          this.glitchTip.addBreadcrumb({
            category: 'storage',
            message: `IndexedDB error in ${operationName}`,
            level: 'error',
            data: {
              operation: operationName,
              error: error instanceof Error ? error.message : String(error),
            },
          });

          // Show user-friendly error message for iOS IndexedDB issues
          if (this.isIOSIndexedDBError(error)) {
            this.errorHandler.showIndexedDBConnectionError().catch((err) => {
              this.logger.error(
                'Failed to show IndexedDB error dialog',
                {
                  operation: 'show_indexeddb_error_dialog_failed',
                },
                err,
              );
            });
          }

          // Try to recover by reinitializing storage
          this._storage = null;
          this.connectionAttempts = 0;

          try {
            await this.init();
            this.logger.info(`Storage recovered successfully for ${operationName}`, {
              operation: 'storage_recovery_success',
              metadata: { operationName },
            });

            // Retry the operation
            return await operation();
          } catch (recoveryError) {
            this.logger.error(
              `Storage recovery failed for ${operationName}`,
              {
                operation: 'storage_recovery_failed',
                metadata: { operationName },
              },
              recoveryError,
            );

            // Update storage status with recovery failure
            this.updateStorageStatus({
              isConnected: false,
              driver: 'unknown',
              connectionAttempts: this.connectionAttempts,
              lastError: `Recovery failed: ${
                recoveryError instanceof Error ? recoveryError.message : String(recoveryError)
              }`,
            });

            this.glitchTip.captureStorageError(recoveryError as Error, {
              operation: 'init',
              storageType: 'ionic_storage',
              additionalData: {
                originalOperation: operationName,
                recoveryAttempt: true,
              },
            });

            throw recoveryError;
          }
        } finally {
          this.isRecovering = false;
        }
      }

      throw error;
    }
  }

  private isIndexedDBConnectionError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error.message || error.toString();
    const connectionErrors = [
      'Connection to Indexed Database server lost',
      'A mutation operation was attempted on a database that did not allow mutations',
      'The database connection is closing',
      'Database has been closed',
      'InvalidStateError',
      'UnknownError',
    ];

    return connectionErrors.some((pattern) => errorMessage.includes(pattern));
  }

  private isIOSIndexedDBError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error.message || error.toString();
    const userAgent = navigator.userAgent || '';

    // Check if this is an iOS device
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (userAgent.includes('Mac') && 'ontouchend' in document);

    // Check for the specific IndexedDB connection error
    const isIndexedDBConnectionLost = errorMessage.includes('Connection to Indexed Database server lost');

    return isIOS && isIndexedDBConnectionLost;
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    return this.queueOperation(async () => {
      return this.executeWithRecovery(async () => {
        if (!this._storage) {
          await this.init();
        }

        // Add breadcrumb for flow tracking
        this.glitchTip.addBreadcrumb({
          category: 'storage',
          message: `setItem: ${key}`,
          level: 'info',
          data: { key, sizeKB: Math.round(new Blob([JSON.stringify(value)]).size / 1024) },
        });

        try {
          await this._storage?.set(key, JSON.stringify(value));
          this.logger.info('Item saved to storage', {
            operation: 'storage_set',
            metadata: { key, valueType: typeof value },
          });
        } catch (error) {
          this.logger.error(
            'Error saving item to storage',
            {
              operation: 'storage_set_failed',
              metadata: { key },
            },
            error,
          );

          // Handle specific errors like quota exceeded
          if (error instanceof Error && error.name === 'QuotaExceededError') {
            this.logger.warn('Storage quota exceeded, attempting cleanup', {
              operation: 'storage_quota_exceeded',
              metadata: { key },
            });

            this.glitchTip.captureStorageError(error, {
              operation: 'write',
              storageType: 'ionic_storage',
              key,
              quotaExceeded: true,
            });
          }

          throw error;
        }
      }, `setItem(${key})`);
    }, `setItem(${key})`);
  }

  async getItem<T>(key: string): Promise<T | null> {
    return this.queueOperation(async () => {
      return this.executeWithRecovery(async () => {
        if (!this._storage) {
          await this.init();
        }

        try {
          const value = await this._storage?.get(key);
          if (value !== null && value !== undefined) {
            try {
              // Try to parse as JSON first (expected format)
              const parsed = JSON.parse(value) as T;
              // this.logger.debug('Item retrieved from storage', {
              //   operation: 'storage_get',
              //   metadata: { key, hasValue: true },
              // });
              return parsed;
            } catch (parseError) {
              // Handle corrupted or non-JSON data
              this.logger.warn('Invalid JSON data found in storage, removing corrupted entry', {
                operation: 'storage_get_json_parse_failed',
                metadata: {
                  key,
                  rawValue: typeof value === 'string' ? value.substring(0, 100) : String(value).substring(0, 100),
                  valueType: typeof value,
                },
              });

              // Remove the corrupted data to prevent future errors
              try {
                await this._storage?.remove(key);
                this.logger.info('Corrupted storage entry removed', {
                  operation: 'storage_corrupted_data_removed',
                  metadata: { key },
                });
              } catch (removeError) {
                this.logger.error(
                  'Failed to remove corrupted storage entry',
                  {
                    operation: 'storage_corrupted_data_remove_failed',
                    metadata: { key },
                  },
                  removeError,
                );
              }

              // Report to Sentry for monitoring
              this.glitchTip.captureStorageError(parseError as Error, {
                operation: 'read',
                storageType: 'ionic_storage',
                key,
                additionalData: {
                  rawValue: typeof value === 'string' ? value.substring(0, 100) : String(value).substring(0, 100),
                  valueType: typeof value,
                  operation: 'json_parse_failed',
                },
              });

              return null;
            }
          }
          this.logger.debug('No item found in storage', {
            operation: 'storage_get',
            metadata: { key, hasValue: false },
          });
          return null;
        } catch (error) {
          this.logger.error(
            'Error retrieving item from storage',
            {
              operation: 'storage_get_failed',
              metadata: { key },
            },
            error,
          );
          return null;
        }
      }, `getItem(${key})`);
    }, `getItem(${key})`);
  }

  async removeItem(key: string): Promise<void> {
    return this.queueOperation(async () => {
      return this.executeWithRecovery(async () => {
        if (!this._storage) {
          await this.init();
        }

        try {
          await this._storage?.remove(key);
          this.logger.info('Item removed from storage', {
            operation: 'storage_remove',
            metadata: { key },
          });
        } catch (error) {
          this.logger.error(
            'Error removing item from storage',
            {
              operation: 'storage_remove_failed',
              metadata: { key },
            },
            error,
          );
          throw error;
        }
      }, `removeItem(${key})`);
    }, `removeItem(${key})`);
  }

  async clear(): Promise<void> {
    return this.queueOperation(async () => {
      return this.executeWithRecovery(async () => {
        if (!this._storage) {
          await this.init();
        }

        try {
          await this._storage?.clear();
          this.logger.info('All storage cleared', {
            operation: 'storage_clear_all',
            metadata: {},
          });
          console.log(await this._storage?.keys());
        } catch (error) {
          this.logger.error(
            'Error clearing storage',
            {
              operation: 'storage_clear_failed',
            },
            error,
          );
          throw error;
        }
      }, 'clear()');
    }, 'clear()');
  }

  async keys(): Promise<string[]> {
    return this.queueOperation(async () => {
      return this.executeWithRecovery(async () => {
        if (!this._storage) {
          await this.init();
        }

        try {
          const keys = (await this._storage?.keys()) || [];
          return keys;
        } catch (error) {
          this.logger.error(
            'Error getting storage keys',
            {
              operation: 'storage_keys_failed',
            },
            error,
          );
          return [];
        }
      }, 'keys()');
    }, 'keys()');
  }

  async length(): Promise<number> {
    return this.queueOperation(async () => {
      return this.executeWithRecovery(async () => {
        if (!this._storage) {
          await this.init();
        }

        try {
          const length = (await this._storage?.length()) || 0;
          return length;
        } catch (error) {
          this.logger.error(
            'Error getting storage length',
            {
              operation: 'storage_length_failed',
            },
            error,
          );
          return 0;
        }
      }, 'length()');
    }, 'length()');
  }

  /**
   * Remove items that match a key pattern (useful for cache cleanup)
   */
  async removeItemsWithPattern(pattern: string): Promise<void> {
    try {
      const keys = await this.keys();
      const keysToRemove = keys.filter((key) => key.includes(pattern));

      for (const key of keysToRemove) {
        await this.removeItem(key);
      }

      if (keysToRemove.length > 0) {
        this.logger.info('Items removed by pattern', {
          operation: 'storage_remove_pattern',
          metadata: { pattern, removedCount: keysToRemove.length },
        });
      }
    } catch (error) {
      this.logger.error(
        'Error removing items by pattern',
        {
          operation: 'storage_remove_pattern_failed',
          metadata: { pattern },
        },
        error,
      );
    }
  }

  /**
   * Clear all i18n related cache (translations and version info)
   */
  async removeLocalization(): Promise<void> {
    return this.executeWithRecovery(async () => {
      try {
        // Clear translation cache
        await this.removeItemsWithPattern('translate-cache');

        // Clear i18n version cache
        await this.removeItemsWithPattern('i18n-manifest');
        await this.removeItemsWithPattern('i18n-hash-');

        this.logger.info('All i18n cache cleared', {
          operation: 'storage_clear_i18n',
          metadata: {},
        });
      } catch (error) {
        this.logger.error(
          'Error clearing i18n cache',
          {
            operation: 'storage_clear_i18n_failed',
          },
          error,
        );
        throw error;
      }
    }, 'removeLocalization()');
  }

  /**
   * Get current storage health status
   */
  async getStorageHealth(): Promise<{
    isHealthy: boolean;
    driver: string;
    connectionAttempts: number;
    canConnect: boolean;
  }> {
    try {
      if (!this._storage) {
        await this.init();
      }

      const driver = this._storage?.driver || 'unknown';
      const canConnect = this._storage !== null;

      return {
        isHealthy: canConnect,
        driver,
        connectionAttempts: this.connectionAttempts,
        canConnect,
      };
    } catch (error) {
      this.logger.error(
        'Error checking storage health',
        {
          operation: 'storage_health_check_failed',
        },
        error,
      );

      return {
        isHealthy: false,
        driver: 'unknown',
        connectionAttempts: this.connectionAttempts,
        canConnect: false,
      };
    }
  }

  private isRecovering = false;

  /**
   * Simple queue for all storage operations to prevent IndexedDB overload
   */
  private queueOperation<T>(operation: () => Promise<T>, operationName: string): Promise<T> {
    const operationResult = this.operationQueue.then(async () => {
      // Add breadcrumb for queued operation
      this.glitchTip.addBreadcrumb({
        category: 'storage_queue',
        message: `Queued operation: ${operationName}`,
        level: 'debug',
        data: { operation: operationName },
      });

      const result = await operation();

      // Small delay between operations to prevent overwhelming IndexedDB
      await new Promise((resolve) => setTimeout(resolve, 50));

      return result;
    });

    this.operationQueue = operationResult.then(
      () => undefined,
      () => undefined,
    );

    return operationResult;
  }

  /**
   * LocalStorage-specific operations for drafts (more reliable than IndexedDB)
   */
  async setItemLocalStorage<T>(key: string, value: T): Promise<void> {
    try {
      // Add breadcrumb for flow tracking
      this.glitchTip.addBreadcrumb({
        category: 'storage',
        message: `setItemLocalStorage: ${key}`,
        level: 'info',
        data: { key, sizeKB: Math.round(new Blob([JSON.stringify(value)]).size / 1024) },
      });

      localStorage.setItem(key, JSON.stringify(value));

      this.logger.info('Item saved to localStorage', {
        operation: 'localstorage_set',
        metadata: { key, valueType: typeof value },
      });
    } catch (error) {
      this.logger.error(
        'Error saving item to localStorage',
        {
          operation: 'localstorage_set_failed',
          metadata: { key },
        },
        error,
      );

      // Handle quota exceeded error
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.logger.warn('LocalStorage quota exceeded', {
          operation: 'localstorage_quota_exceeded',
          metadata: { key },
        });

        this.glitchTip.captureStorageError(error, {
          operation: 'write',
          storageType: 'localStorage',
          key,
          quotaExceeded: true,
        });
      }

      throw error;
    }
  }

  async getItemLocalStorage<T>(key: string): Promise<T | null> {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        try {
          const parsed = JSON.parse(value) as T;
          return parsed;
        } catch (parseError) {
          this.logger.warn('Invalid JSON data found in localStorage, removing corrupted entry', {
            operation: 'localstorage_get_json_parse_failed',
            metadata: {
              key,
              rawValue: value.substring(0, 100),
            },
          });

          // Remove corrupted data
          localStorage.removeItem(key);

          this.glitchTip.captureStorageError(parseError as Error, {
            operation: 'read',
            storageType: 'localStorage',
            key,
            additionalData: {
              rawValue: value.substring(0, 100),
              operation: 'json_parse_failed',
            },
          });

          return null;
        }
      }
      return null;
    } catch (error) {
      this.logger.error(
        'Error retrieving item from localStorage',
        {
          operation: 'localstorage_get_failed',
          metadata: { key },
        },
        error,
      );
      return null;
    }
  }

  async removeItemLocalStorage(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
      this.logger.info('Item removed from localStorage', {
        operation: 'localstorage_remove',
        metadata: { key },
      });
    } catch (error) {
      this.logger.error(
        'Error removing item from localStorage',
        {
          operation: 'localstorage_remove_failed',
          metadata: { key },
        },
        error,
      );
      throw error;
    }
  }

  async keysLocalStorage(): Promise<string[]> {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      this.logger.error(
        'Error getting localStorage keys',
        {
          operation: 'localstorage_keys_failed',
        },
        error,
      );
      return [];
    }
  }

  /**
   * Remove items from localStorage that match a key pattern
   */
  async removeItemsWithPatternLocalStorage(pattern: string): Promise<void> {
    try {
      const keys = await this.keysLocalStorage();
      const keysToRemove = keys.filter((key) => key.includes(pattern));

      for (const key of keysToRemove) {
        await this.removeItemLocalStorage(key);
      }

      if (keysToRemove.length > 0) {
        this.logger.info('Items removed by pattern from localStorage', {
          operation: 'localstorage_remove_pattern',
          metadata: { pattern, removedCount: keysToRemove.length },
        });
      }
    } catch (error) {
      this.logger.error(
        'Error removing items by pattern from localStorage',
        {
          operation: 'localstorage_remove_pattern_failed',
          metadata: { pattern },
        },
        error,
      );
    }
  }
}
